<template>
  <vx-card no-shadow>
    <div class="vx-row">
      <div class="vx-col w-full md:w-1/2">
        <!-- Col Header -->
        <div class="flex items-end" v-if="showHeader">
          <feather-icon icon="InfoIcon" class="mr-2" svgClasses="w-5 h-5" />
          <span class="leading-none font-medium">{{ $t('users.information') }}</span>
        </div>
      </div>
      <vs-alert class="text-center md:mx-10 my-5" :title="$t('messages.your_user_id')" color="success">
        <div class="flex  justify-center gap-5 items-center">
          <div class="text-center">{{ dataLocal.id }}</div>
          <vs-button
            icon-pack="feather"
            icon="icon-copy"
            size="small"
            type="gradient"
            color="success"
            class="mr-2"
            @click="copyId"
          >
          </vs-button>
        </div>
      </vs-alert>
      <div class="vx-col w-full md:w-1/2">
        <!-- Col Content -->
        <div>
          <div class="w-full mt-4">
            <label class="vs-input--label">{{ $t('users.national_id') }}</label>

            <vs-input
              name="nationalId"
              id="nationalId"
              v-model="dataLocal.details.nationalId"
              @keyup="dataLocal.details.nationalId = translateNumbers(dataLocal.details.nationalId)"
              v-validate="'nationalId'"
              :data-vv-as="$t('users.national_id')"
              class="w-full"
            />
            <span class="text-danger text-sm" v-show="errors.has('nationalId')">{{
              errors.first('nationalId')
            }}</span>
          </div>

          <!--Birthday -->
          <div class="w-full mt-4">
            <label class="vs-input--label">{{ $t('users.birthday') }}</label>

            <date-picker
              type="date"
              v-model="dataLocal.details.birthday"
              element="birthday"
              view="year"
              :max="
                $moment()
                  .subtract(10, 'years')
                  .format('YYYY-MM-DDTHH:mm:ss.SSSZ')
              "
              :min="
                $moment()
                  .subtract(100, 'years')
                  .format('YYYY-MM-DDTHH:mm:ss.SSSZ')
              "
            />
            <vs-input
              name="birthday"
              id="birthday"
              :value="
                $moment(dataLocal.details.birthday)
                  .locale($vs.rtl ? 'fa' : 'en')
                  .format('DD MMMM YYYY')
              "
              v-validate="'required'"
              class="w-full"
            />
            <span class="text-danger text-sm" v-show="errors.has('birthday')">{{
              errors.first('birthday')
            }}</span>
          </div>

          <!-- Gender -->
          <div class="mt-4">
            <label class="text-sm">{{ $t('users.genders.gender') }}</label>
            <div class="mt-2">
              <vs-radio v-model="dataLocal.details.gender" vs-name="gender" vs-value="male" class="mr-4">{{
                $t('users.genders.male')
              }}</vs-radio>
              <vs-radio v-model="dataLocal.details.gender" vs-name="gender" vs-value="female" class="mr-4">{{
                $t('users.genders.female')
              }}</vs-radio>
              <vs-radio v-model="dataLocal.details.gender" vs-name="gender" vs-value="other">{{
                $t('users.genders.other')
              }}</vs-radio>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Addresses Table -->
    <div class="vx-row">
      <div class="vx-col w-full">
        <!-- Col Header -->
        <div class="flex items-end md:mt-5 mb-5 mt-base">
          <feather-icon icon="MapPinIcon" class="mr-2" svgClasses="w-5 h-5" />
          <span class="leading-none font-medium">{{ $t('users.address') }}</span>
        </div>
        <!-- Col Content -->
        <vs-table ref="table" :data="dataLocal.addresses">
          <template slot="thead">
            <vs-th sort-key="address">{{ $t('users.addresses.address') }}</vs-th>
            <vs-th>{{ $t('forms.actions') }}</vs-th>
          </template>

          <template slot-scope="{ data }">
            <tbody v-if="data.length">
              <vs-tr :data="tr" :key="indextr" v-for="(tr, indextr) in data">
                <vs-td>
                  <address-string :address="tr" hide-label />
                </vs-td>
                <vs-td>
                  <feather-icon
                    icon="TrashIcon"
                    svgClasses="w-5 h-5 hover:text-danger stroke-current"
                    class="ml-2"
                    @click.stop="openConfirm(indextr)"
                  />
                </vs-td>

                <template slot="expand">
                  <div class="w-full">
                    <div class="vx-row w-full">
                      <div class="vx-col w-full md:w-1/4">
                        <div class="font-semibold">{{ $t('users.location') }}:</div>
                        <div>{{ $t('users.lat') }}: {{ tr.location.lat | persian_digits }}</div>
                        <div>{{ $t('users.lon') }}: {{ tr.location.lon | persian_digits }}</div>
                      </div>
                      <div class="vx-col w-full md:w-3/4">
                        <open-street-map
                          view-only
                          :markers="[{ latlng: tr.location, name: tr.alias }]"
                          :zoom="15"
                          disable-fit
                          :center="tr.location"
                        />
                      </div>
                    </div>
                    <div class="vx-row w-full" v-if="tr.description">
                      <div class="vx-col">
                        <div class="font-semibold mt-2">{{ $t('places.description') }}:</div>
                        <div>{{ tr.description }}</div>
                      </div>
                    </div>
                  </div>
                </template>
              </vs-tr>
            </tbody>
          </template>
        </vs-table>
      </div>
    </div>
    <!-- Add new Address Form -->
    <vs-button
      v-if="!showAddAddress"
      class="w-full"
      color="success"
      type="border"
      icon-pack="feather"
      icon="icon-plus"
      @click="showAddAddress = true"
    >
      {{ $t('forms.add') }} {{ $t('users.address') }}
    </vs-button>
    <template v-if="showAddAddress">
      <open-street-map class="m-3" v-model="mapData"></open-street-map>
      <vs-row v-if="showAddressInputs">
        <!-- Content-->
        <vs-input
          v-model="newAddress.province"
          :label="$t('users.addresses.province')"
          class="mt-5 w-full md:w-1/4 pr-3"
          name="new-province"
          :disabled="(mapData.opStatus === 'loaded' && mapData.province) || mapData.opStatus === 'clear'"
          :placeholder="mapData.opStatus === 'loaded' ? '' : $t('messages.autofill')"
        />
        <vs-input
          v-model="newAddress.district"
          :label="$t('users.addresses.district')"
          class="mt-5 w-full md:w-1/4 pr-3"
          name="new-district"
          :disabled="(mapData.opStatus === 'loaded' && mapData.district) || mapData.opStatus === 'clear'"
          :placeholder="mapData.opStatus === 'loaded' ? '' : $t('messages.autofill')"
        />
        <vs-input
          v-model="newAddress.county"
          :label="$t('users.addresses.county')"
          class="mt-5 w-full md:w-1/4 pr-3"
          name="new-county"
          :disabled="(mapData.opStatus === 'loaded' && mapData.county) || mapData.opStatus === 'clear'"
          :placeholder="mapData.opStatus === 'loaded' ? '' : $t('messages.autofill')"
        />
        <vs-input
          v-model="newAddress.city"
          :label="$t('users.addresses.city')"
          class="mt-5 w-full md:w-1/4 pr-3"
          name="new-city"
          :disabled="(mapData.opStatus === 'loaded' && mapData.city) || mapData.opStatus === 'clear'"
          :placeholder="mapData.opStatus === 'loaded' ? '' : $t('messages.autofill')"
        />
        <vs-input
          v-model="newAddress.borough"
          :label="$t('users.addresses.borough')"
          class="mt-5 w-full md:w-1/4 pr-3"
          name="new-borough"
          :disabled="(mapData.opStatus === 'loaded' && mapData.borough) || mapData.opStatus === 'clear'"
          :placeholder="mapData.opStatus === 'loaded' ? '' : $t('messages.autofill')"
        />
        <vs-input
          v-model="newAddress.village"
          :label="$t('users.addresses.village')"
          class="mt-5 w-full md:w-1/4 pr-3"
          name="new-village"
        />
        <vs-input
          v-model="newAddress.street"
          :label="$t('users.addresses.street')"
          class="mt-5 w-full md:w-1/4 pr-3"
          name="new-street"
        />
        <vs-input
          v-model="newAddress.line"
          :label="$t('users.addresses.line')"
          class="mt-5 w-full md:w-1/4 pr-3"
          name="new-line"
        />
        <vs-input
          v-model="newAddress.address"
          :label="$t('users.addresses.address')"
          class="mt-5 w-full md:w-1/4 pr-3"
          name="new-address"
        />
        <vs-input
          v-model="newAddress.postal"
          :label="$t('users.addresses.postal')"
          class="mt-5 w-full md:w-1/4 pr-3"
          name="new-postal"
        />
      </vs-row>
      <template v-else-if="mapData.province">
        <address-string :address="newAddress" />
        <vs-checkbox v-model="showAddressInputs" @click="collapseClasses = ''" class="mt-2">{{
          $t('messages.complete_address')
        }}</vs-checkbox>
      </template>
      <div class="mt-8 pr-3">
        <vs-button
          :disabled="!mapData.province"
          class="w-full"
          color="success"
          type="border"
          @click="addNewAddress"
        >
          {{ $t('forms.save') }}
        </vs-button>
      </div>
    </template>
    <!-- Save & Reset Button -->
    <div class="vx-row">
      <div class="vx-col w-full">
        <div class="mt-8 flex flex-wrap items-center justify-end">
          <vs-button class="ml-auto mt-2" @click="saveChanges">{{ $t('forms.update') }}</vs-button>
          <vs-button class="ml-4 mt-2" type="border" color="warning" @click="resetData">{{
            $t('forms.reset')
          }}</vs-button>
        </div>
      </div>
    </div>
  </vx-card>
</template>

<script>
import OpenStreetMap from '@/views/components/maps/OpenStreetMap'
import AddressString from '@/views/components/maps/AddressString'
import i18n from '@/i18n/i18n'
import { TranslateNumbers } from '@/utils'

export default {
  props: {
    data: {
      type: Object,
      required: true
    },
    showHeader: {
      type: Boolean,
      default: false
    }
  },
  components: {
    AddressString,
    OpenStreetMap
  },
  data() {
    return {
      mapData: {},
      dataLocal: JSON.parse(JSON.stringify(this.data)),
      newAddress: {
        province: null,
        district: null,
        county: null,
        city: null,
        borough: null,
        village: null,
        postal: null,
        street: null,
        line: null,
        address: null
      },
      showAddAddress: false,
      showAddressInputs: false
    }
  },
  watch: {
    mapData(value) {
      this.newAddress.location = value.location
      this.newAddress.province = value.province
      this.newAddress.district = value.district ? value.district : ''
      this.newAddress.county = value.county
      this.newAddress.city = value.city ? value.city : ''
      this.newAddress.borough = value.borough ? value.borough : ''
    }
  },
  methods: {
    addNewAddress() {
      this.dataLocal.addresses.push(this.newAddress)
      this.newAddress = {}
      this.mapData = {}
    },
    openConfirm(index) {
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: this.$t('button.delete'),
        text: this.$t('messages.are_you_sure'),
        accept: () => {
          delete this.dataLocal.addresses.splice(index, 1)
        },
        acceptText: this.$t('button.delete'),
        cancelText: this.$t('button.cancel')
      })
    },
    saveChanges() {
      // eslint-disable-next-line no-unused-vars
      const { id, ...userData } = this.dataLocal
      if (userData.details) delete userData.details['_id']
      if (userData.location) delete userData.location['_id']
      delete userData['password']
      this.$store
        .dispatch('auth/editProfile', userData)
        // eslint-disable-next-line no-unused-vars
        .then(res => {
          this.$store.commit('UPDATE_USER_INFO', {
            displayName: `${res.data.details.firstName} ${res.data.details.lastName}`,
            ...res.data
          })
          this.$vs.notify({
            color: 'success',
            title: this.$t('forms.successfully_edited')
          })
        })
    },
    resetData() {
      this.dataLocal = JSON.parse(JSON.stringify(this.data))
    },
    copyId() {
      const el = document.createElement('textarea')
      el.value = this.dataLocal.id
      el.setAttribute('readonly', '')
      el.style.position = 'absolute'
      el.style.left = '-9999px'
      document.body.appendChild(el)
      const selected = document.getSelection().rangeCount > 0 ? document.getSelection().getRangeAt(0) : false
      el.select()
      document.execCommand('copy')
      document.body.removeChild(el)
      if (selected) {
        document.getSelection().removeAllRanges()
        document.getSelection().addRange(selected)
      }
      this.$notifySuccess(i18n.t('messages.your_user_id_copied'))
    },
    translateNumbers(val) {
      return TranslateNumbers(val)
    }
  }
}
</script>
